// PricingPage.jsx
// PricingPage.jsx

import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Tabs,
  Tab,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Stack,
  useTheme,
} from "@mui/material";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import MonetizationOnOutlinedIcon from "@mui/icons-material/MonetizationOnOutlined";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import PricingPageStyles from "./PricingPage.styles";
import useSubscribe from "../../hooks/useSubscribe";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth, ROLES } from "../../hooks/useAuth";

import axios from "axios";
import { loadStripe } from "@stripe/stripe-js";

const API_URL = process.env.REACT_APP_API_URL;
const STRIPE_PUBLIC_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

/* ===================== Helpers & Constants ===================== */

const TAB_OWNERS = "owners";
const TAB_MANAGERS = "managers";
const VALID_TABS = [TAB_OWNERS, TAB_MANAGERS];

const normalizeTab = (val) => {
  const v = (val || "").toString().trim().toLowerCase();
  if (v === TAB_MANAGERS || v === "manager" || v === "pm") return TAB_MANAGERS;
  return TAB_OWNERS;
};

// Map various stored/legacy values into the two pricing roles we care about here.
// Everything in the "agent/owner" lane uses the Owners tab, managers use Managers tab.
const normalizeRole = (val) => {
  const v = (val || "").toString().trim().toLowerCase();
  if (v === "landlord" || v === "owner" || v === "agent" || v === ROLES.AGENT) return "agent";
  if (v === "pm" || v === "manager" || v === "managers" || v === ROLES.MANAGER) return "manager";
  return null;
};

// Read role and/or tab from URL with broad alias support
const readQueryOverrides = () => {
  try {
    const qs = new URLSearchParams(window.location.search);
    const roleRaw =
      qs.get("userType") ??
      qs.get("role") ??
      qs.get("type") ??
      qs.get("r") ??
      null;

    const tabRaw = qs.get("tab") ?? qs.get("t") ?? null;

    const role = normalizeRole(roleRaw);
    const tab = tabRaw ? normalizeTab(tabRaw) : null;

    console.log("[PricingPage] Query overrides →", { roleRaw, tabRaw, role, tab });
    return { role, tab };
  } catch (err) {
    console.error("[PricingPage] Failed to read query overrides:", err);
    return { role: null, tab: null };
  }
};

const ALL_TABS = [
  { key: TAB_OWNERS, label: "Property Owners", icon: <MonetizationOnOutlinedIcon /> },
  { key: TAB_MANAGERS, label: "Property Managers", icon: <ManageAccountsOutlinedIcon /> },
];

const isSubscriptionPlan = (id = "") => /^pm_|^owner_/i.test(id);
const isOwnerPlan = (id = "") => /^owner_/i.test(id);

const roleDefaultTab = (role) => (role === "manager" ? TAB_MANAGERS : TAB_OWNERS);

/* ===================== Component ===================== */

const PricingPage = () => {
  const theme = useTheme();
  const { startSubscription, loading: hookLoading, error: hookError } = useSubscribe();
  const { currentUser } = useAuth(); // from AuthContext (already normalized userType)
  const location = useLocation();
  const navigate = useNavigate();

  // Derive basic auth flags from context (AuthProvider hides children until ready)
  const authed = !!currentUser;
  const authChecked = true;

  // Role
  const [roleLoading, setRoleLoading] = useState(true);
  const [userType, setUserType] = useState(null); // "agent" | "manager" | null

  const [{ role: queryRole, tab: queryTab }] = useState(readQueryOverrides());

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!authed) {
        console.log("[PricingPage] Not authed, skipping role resolution.");
        if (mounted) {
          setUserType(null);
          setRoleLoading(false);
        }
        return;
      }

      // 1) URL override (explicit) – useful for QA: ?userType=agent|manager|landlord|owner
      if (queryRole) {
        console.log("[PricingPage] Using query override role:", queryRole);
        const normalized = normalizeRole(queryRole);
        if (mounted) {
          setUserType(normalized);
          setRoleLoading(false);
        }
        return;
      }

      // 2) Primary source: AuthContext userType (already normalized from Firestore)
      const rawFromContext = currentUser?.userType;
      const normalizedFromContext = normalizeRole(rawFromContext);
      console.log("[PricingPage] Context userType raw:", rawFromContext, "→", normalizedFromContext);

      // 3) Fallback: if we truly can't determine, treat as "agent/owner"
      const resolved = normalizedFromContext || "agent";
      console.log("[PricingPage] Final resolved role from context:", resolved);

      if (mounted) {
        setUserType(resolved);
        setRoleLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [authed, queryRole, currentUser]);

  // Tabs visible strictly by role
  const visibleTabs = useMemo(() => {
    const arr =
      userType === "agent"
        ? ALL_TABS.filter((t) => t.key === TAB_OWNERS)
        : userType === "manager"
          ? ALL_TABS.filter((t) => t.key === TAB_MANAGERS)
          : [];
    console.log("[PricingPage] visibleTabs ->", arr.map((t) => t.key));
    return arr;
  }, [userType]);

  // Active tab
  const [activeTab, setActiveTab] = useState(null);

  // Set default when role known, honoring query tab if provided and allowed
  useEffect(() => {
    if (authChecked && !roleLoading && userType) {
      const preferred = queryTab || roleDefaultTab(userType);
      const def = normalizeTab(preferred);

      const isVisible = visibleTabs.some((t) => t.key === def);
      const chosen = isVisible ? def : (visibleTabs[0]?.key || null);

      console.log("[PricingPage] Setting default activeTab:", { preferred, def, chosen, userType });
      setActiveTab(chosen);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authChecked, roleLoading, userType, visibleTabs, queryTab]);

  // Guard: normalize if something weird slips in
  useEffect(() => {
    if (activeTab && !VALID_TABS.includes(activeTab)) {
      const fixed = normalizeTab(activeTab);
      console.warn("[PricingPage] Invalid activeTab, normalizing:", activeTab, "→", fixed);
      setActiveTab(fixed);
    }
  }, [activeTab]);

  // Always provide a tab that exists for current visibleTabs
  const safeActiveTab = useMemo(() => {
    const norm = activeTab ? normalizeTab(activeTab) : null;
    const has = norm && visibleTabs.some((t) => t.key === norm);
    const chosen = has ? norm : (visibleTabs[0]?.key || null);
    if (activeTab !== chosen) {
      console.log("[PricingPage] safeActiveTab correcting", {
        activeTab,
        norm,
        chosen,
        visible: visibleTabs.map((t) => t.key),
      });
    }
    return chosen;
  }, [visibleTabs, activeTab]);

  const handleTab = (_e, value) => {
    const norm = normalizeTab(value);
    console.log("[PricingPage] Tab switched:", value, "→", norm);
    if (visibleTabs.some((t) => t.key === norm)) {
      setActiveTab(norm);
    } else {
      console.warn("[PricingPage] Blocked tab switch to", norm, "— not visible for role", userType);
    }
  };

  /* ===================== Plans ===================== */

  const ownerPlans = [
    {
      id: "owner_basic_9_95_setup_5",
      title: "Owner Basic",
      priceStr: "$0.99",
      cadence: "/ month",
      // setup: "$5 one-time setup",
      badge: "Popular",
      subtitle: "Flat rate for general access + tech fee of $5.99 per property/per month.",
      note: "*Tech fee of $5.99 waived when tenant opts-in for active remote access subscription and membership savings benefits on their portal during their tenancy",
      bullets: [
        "Full access management suite",
        "Automated recurring billing",
        "Portfolio & team ready",
        "Priority support",
      ],
      cta: "Start Basic",
      planKey: "owner_basic",
    },
    // {
    //   id: "owner_basic_warranty_11_95_setup_5",
    //   title: "Owner Basic + Warranty Labor",
    //   priceStr: "$11.95",
    //   cadence: "/ month",
    //   setup: "$5 one-time setup",
    //   badge: "Best value",
    //   subtitle:
    //     "$5 setup + $11.95/month basic + labor reimbursement to repair/replace devices under warranty (up to $500/year).",
    //   bullets: [
    //     "Everything in Basic",
    //     "Labor reimbursement up to $500/year",
    //     "Fewer out-of-pocket surprises",
    //     "Priority claim handling",
    //   ],
    //   cta: "Start Basic + Warranty",
    //   planKey: "owner_warranty",
    // },
  ];

  const managerPlan = {
    id: "pm_access_management_24_95",
    title: "Tenant Access - General Access Subscription",
    priceStr: "$0.99",
    cadence: "/ month",
    badge: "Managers",
    subtitle: "Flat rate for general access + tech fee of $5.99 per property/per month.",
    note: "*Tech fee of $5.99 waived when tenant opts-in for active remote access subscription and membership savings benefits on their portal during their tenancy",
    bullets: [
      "Full access management suite",
      "Automated recurring billing",
      "Portfolio & team ready",
      "Priority support",
    ],
    cta: "Start Manager Plan",
  };

  /* ===================== Checkout ===================== */

  const [ownerSubmitting, setOwnerSubmitting] = useState(false);
  const [ownerError, setOwnerError] = useState("");

  const startOwnerCheckout = async (planKey) => {
    console.log("[PricingPage] Starting owner checkout:", planKey);
    try {
      setOwnerError("");
      if (!currentUser?.uid) {
        console.error("[PricingPage] No user UID for checkout!");
        setOwnerError("User authentication is missing.");
        return;
      }
      setOwnerSubmitting(true);

      const resp = await axios.post(`${API_URL}/api/subscribe/owner`, {
        uid: currentUser.uid,
        plan: planKey,
        billingCycle: "monthly",
      });

      console.log("[PricingPage] Checkout response:", resp.data);

      const { sessionId } = resp.data || {};
      if (!sessionId) throw new Error("No session ID returned from API.");

      if (!STRIPE_PUBLIC_KEY) {
        throw new Error("Stripe public key is not configured. Please set REACT_APP_STRIPE_PUBLIC_KEY or REACT_APP_STRIPE_PUBLISHABLE_KEY in your .env file.");
      }

      const stripe = await loadStripe(STRIPE_PUBLIC_KEY);
      if (!stripe) throw new Error("Stripe.js failed to load!");

      const { error } = await stripe.redirectToCheckout({ sessionId });
      if (error) setOwnerError(error.message || "Checkout failed.");
    } catch (e) {
      console.error("[PricingPage] Checkout error:", e);
      setOwnerError(e?.response?.data?.error || e.message || "Checkout failed.");
    } finally {
      setOwnerSubmitting(false);
    }
  };

  const startMonthlySubscription = () => {
    console.log("[PricingPage] Starting manager subscription flow");
    startSubscription("monthly");
  };

  /* ===================== UI Bits ===================== */

  const FeatureList = ({ items }) => (
    <List sx={PricingPageStyles.featuresList}>
      {items.map((txt, i) => (
        <ListItem key={i} sx={PricingPageStyles.featureItem}>
          <ListItemIcon sx={PricingPageStyles.featureIcon}>
            <CheckCircleRoundedIcon />
          </ListItemIcon>
          <ListItemText primary={txt} />
        </ListItem>
      ))}
    </List>
  );

  const PlanCard = ({ plan, color = "primary" }) => {
    const isOwner = isOwnerPlan(plan.id);
    const isBusy = isOwner ? ownerSubmitting : hookLoading;

    const onClick = () => {
      console.log("[PricingPage] Plan clicked:", plan.id, "isOwner?", isOwner);
      if (isOwner) {
        const planKey = plan.planKey || (plan.id.includes("warranty") ? "owner_warranty" : "owner_basic");
        return startOwnerCheckout(planKey);
      }
      if (isSubscriptionPlan(plan.id)) return startMonthlySubscription();
      return null;
    };

    return (
      <Card elevation={12} sx={PricingPageStyles.planCard}>
        <CardContent>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography variant="h5" sx={PricingPageStyles.planTitle}>
              {plan.title}
            </Typography>
            {plan.badge && <Chip color={color} label={plan.badge} sx={PricingPageStyles.badge} />}
          </Stack>

          <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 0.5 }}>
            <Typography variant="h3" sx={PricingPageStyles.planPrice}>
              {plan.priceStr}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {plan.cadence}
            </Typography>
          </Stack>

          {plan.setup && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {plan.setup}
            </Typography>
          )}

          <Typography variant="body1" sx={PricingPageStyles.planSubtitle}>
            {plan.subtitle} <p style={{ fontSize: "12px" }}>{plan.note}</p>
          </Typography>

          <Divider sx={PricingPageStyles.divider} />

          <FeatureList items={plan.bullets} />
        </CardContent>

        <CardActions sx={PricingPageStyles.cardActions}>
          <Button
            onClick={onClick}
            variant="contained"
            size="large"
            fullWidth
            disabled={isBusy}
            sx={PricingPageStyles.ctaButton}
          >
            {isBusy ? "Processing..." : plan.cta}
          </Button>
        </CardActions>
      </Card>
    );
  };

  const OwnersSection = () => (
    <Grid container spacing={3} justifyContent="center">
      {ownerPlans.map((p, i) => (
        <Grid item xs={12} md={6} lg={5} key={p.id}>
          <PlanCard plan={p} color={i === 1 ? "success" : "primary"} />
        </Grid>
      ))}
    </Grid>
  );

  const ManagersSection = () => (
    <Grid container spacing={3} justifyContent="center">
      <Grid item xs={12} md={7} lg={6}>
        <PlanCard plan={managerPlan} color="info" />
      </Grid>
    </Grid>
  );

  /* ===================== Render ===================== */

  const loadingUI = (
    <Box sx={PricingPageStyles.pageBg(theme)}>
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        <Stack spacing={2}>
          <Skeleton variant="text" width={240} height={56} />
          <Skeleton variant="rounded" height={40} width={260} />
          <Skeleton variant="rounded" height={220} />
        </Stack>
      </Container>
    </Box>
  );

  if (!authChecked || roleLoading) {
    console.log("[PricingPage] Waiting for auth/role to resolve...");
    return loadingUI;
  }

  if (!authed) {
    console.log("[PricingPage] Not logged in, showing login screen");
    return (
      <Box sx={PricingPageStyles.pageBg(theme)}>
        <Container maxWidth="sm" sx={{ py: { xs: 8, md: 12 } }}>
          <Card elevation={10} sx={{ p: 4, textAlign: "center", borderRadius: 4 }}>
            <Typography variant="h4" sx={{ mb: 1 }}>
              Please log in to view pricing
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              You need an account to see subscription options.
            </Typography>
            <Button component={RouterLink} to="/login" size="large" variant="contained" sx={PricingPageStyles.ctaButton}>
              Go to Login
            </Button>
          </Card>
        </Container>
      </Box>
    );
  }

  if (!userType || !safeActiveTab) {
    console.log("[PricingPage] Holding UI until role & tab ready →", { userType, activeTab, safeActiveTab });
    return loadingUI;
  }

  console.log("[PricingPage] RENDER → userType:", userType, "| activeTab:", activeTab, "| safeActiveTab:", safeActiveTab);

  return (
    <Box sx={PricingPageStyles.pageBg(theme)}>
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, gap: 2 }}>
          <Box />
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => {
              const from = location.state?.from;
              if (typeof from === "string" && from.trim()) navigate(from);
              else navigate(-1);
            }}
            sx={{ textTransform: "none" }}
          >
            Back
          </Button>
        </Box>
        <Typography variant="h2" sx={PricingPageStyles.pageTitle}>
          Pricing
        </Typography>
        <Typography variant="h6" sx={PricingPageStyles.pageSubtitle}>
          Clear options for agents (landlords) and managers — straightforward subscriptions.
        </Typography>

        <Box sx={PricingPageStyles.tabsWrap}>
          <Tabs value={safeActiveTab} onChange={handleTab} sx={PricingPageStyles.tabs}>
            {visibleTabs.map((t) => (
              <Tab
                key={t.key}
                value={t.key}
                icon={t.icon}
                iconPosition="start"
                label={t.label}
                sx={PricingPageStyles.tab}
              />
            ))}
          </Tabs>
        </Box>

        <Box sx={{ mt: 5 }}>
          {safeActiveTab === TAB_OWNERS && <OwnersSection />}
          {safeActiveTab === TAB_MANAGERS && <ManagersSection />}
        </Box>

        {(hookError || ownerError) && (
          <Typography color="error" sx={{ mt: 3, textAlign: "center" }}>
            {ownerError || hookError}
          </Typography>
        )}
      </Container>
    </Box>
  );
};

export default PricingPage;




// import React, { useEffect, useMemo, useState } from "react";
// import {
//   Box,
//   Container,
//   Typography,
//   Grid,
//   Card,
//   CardContent,
//   CardActions,
//   Button,
//   Tabs,
//   Tab,
//   Chip,
//   Divider,
//   List,
//   ListItem,
//   ListItemIcon,
//   ListItemText,
//   Skeleton,
//   Stack,
//   useTheme,
// } from "@mui/material";
// import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
// import MonetizationOnOutlinedIcon from "@mui/icons-material/MonetizationOnOutlined";
// import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
// import PricingPageStyles from "./PricingPage.styles";
// import useSubscribe from "../../hooks/useSubscribe";
// import { Link as RouterLink } from "react-router-dom";

// // Auth
// import { getAuth, onAuthStateChanged } from "firebase/auth";

// // Read userType from Firebase/local via helper
// import { getUserType as getStoredFirebaseUserType } from "../../utils/userType";

// // --- NEW: only for Owner plans checkout (keeps your landlord flow untouched)
// import axios from "axios";
// import { loadStripe } from "@stripe/stripe-js";

// // If you already expose these via env, swap to import.meta.env.VITE_* accordingly.
// // const API_URL = "http://localhost:9000";
// // load API url from env
// const API_URL = process.env.REACT_APP_API_URL;
// // const STRIPE_PUBLIC_KEY =
// //   "pk_test_51S0iOxF9sKc1ko7nl8kfs6cG2BJXo1EhC5U9pvOhJADzBg8Qd5JaEPYIVTgPJl9mq3rUr1dpQavRCZFM4e24bsRu00lhKowmsm";

// // load public key from .env
// const STRIPE_PUBLIC_KEY = process.env.REACT_APP_STRIPE_PUBLIC_KEY;

// /** -------------------------------------------------------------
//  * Role resolution (two roles only):
//  *  - "agent" (landlord) | "manager"
//  *  - QA override via ?userType=agent|manager (and legacy ?userType=landlord -> agent)
//  *  - Fallback to "manager"
//  * ------------------------------------------------------------*/
// const DEFAULT_ROLE = "manager";

// const normalizeRole = (val) => {
//   const v = (val || "").toLowerCase();
//   if (v === "landlord") return "agent"; // legacy alias
//   return v === "agent" || v === "manager" ? v : null;
// };

// const readQueryOverride = () => {
//   try {
//     const qs = new URLSearchParams(window.location.search);
//     return normalizeRole(qs.get("userType"));
//   } catch {
//     return null;
//   }
// };

// const ALL_TABS = [
//   { key: "owners", label: "Property Owners", icon: <MonetizationOnOutlinedIcon /> },
//   { key: "managers", label: "Property Managers", icon: <ManageAccountsOutlinedIcon /> },
// ];

// // identify subscription plans (all plans on this page)
// const isSubscriptionPlan = (id = "") => /^pm_|^owner_/i.test(id);

// // Identify owner plans to route them to owner checkout (keeps landlord flow intact)
// const isOwnerPlan = (id = "") => /^owner_/i.test(id);

// const PricingPage = () => {
//   const theme = useTheme();
//   const { startSubscription, loading: hookLoading, error: hookError } = useSubscribe();

//   // ---------- Auth gate -----------
//   const [authed, setAuthed] = useState(false);
//   const [authChecked, setAuthChecked] = useState(false);

//   useEffect(() => {
//     const auth = getAuth();
//     const unsub = onAuthStateChanged(auth, (u) => {
//       setAuthed(!!u);
//       setAuthChecked(true);
//     });
//     return () => unsub();
//   }, []);

//   // ---------- Resolve userType from Firebase/local -----------
//   const [roleLoading, setRoleLoading] = useState(true);
//   const [userType, setUserType] = useState(DEFAULT_ROLE);
//   const [currentUser, setCurrentUser] = useState(null); // for owner checkout UID

//   useEffect(() => {
//     const auth = getAuth();
//     const unsub = onAuthStateChanged(auth, (u) => setCurrentUser(u || null));
//     return () => unsub();
//   }, []);

//   useEffect(() => {
//     let mounted = true;
//     (async () => {
//       if (!authed) {
//         setRoleLoading(false);
//         return;
//       }
//       const override = readQueryOverride();
//       if (override) {
//         if (mounted) {
//           setUserType(override);
//           setRoleLoading(false);
//         }
//         return;
//       }
//       const fromHelper = await getStoredFirebaseUserType(); // "agent" | "manager" | null
//       const resolved = normalizeRole(fromHelper) || DEFAULT_ROLE;
//       if (mounted) {
//         setUserType(resolved);
//         setRoleLoading(false);
//       }
//     })();
//     return () => {
//       mounted = false;
//     };
//   }, [authed]);

//   // ---------- Tabs visibility based on role ------------------
//   const visibleTabs = useMemo(() => {
//     if (userType === "agent") return ALL_TABS.filter((t) => t.key === "owners");
//     if (userType === "manager") return ALL_TABS.filter((t) => t.key === "managers");
//     return ALL_TABS;
//   }, [userType]);

//   const [activeTab, setActiveTab] = useState(visibleTabs[0]?.key || "owners");
//   useEffect(() => {
//     if (!visibleTabs.find((t) => t.key === activeTab)) {
//       setActiveTab(visibleTabs[0]?.key || "owners");
//     }
//   }, [visibleTabs, activeTab]);

//   // -------- Subscription Plans ---------------------------------------------
//   const ownerPlans = [
//     {
//       id: "owner_basic_9_95_setup_5",
//       title: "Owner Basic",
//       priceStr: "$9.95",
//       cadence: "/ month",
//       setup: "$5 one-time setup",
//       badge: "Popular",
//       subtitle: "$5 setup + $9.95/month basic owner subscription.",
//       bullets: ["Core access management", "Owner dashboard", "Standard support"],
//       cta: "Start Basic",
//       // map to backend plan key
//       planKey: "owner_basic",
//     },
//     {
//       id: "owner_basic_warranty_11_95_setup_5",
//       title: "Owner Basic + Warranty Labor",
//       priceStr: "$11.95",
//       cadence: "/ month",
//       setup: "$5 one-time setup",
//       badge: "Best value",
//       subtitle:
//         "$5 setup + $11.95/month basic + labor reimbursement to repair/replace devices under warranty (up to $500/year).",
//       bullets: [
//         "Everything in Basic",
//         "Labor reimbursement up to $500/year",
//         "Fewer out-of-pocket surprises",
//         "Priority claim handling",
//       ],
//       cta: "Start Basic + Warranty",
//       planKey: "owner_warranty",
//     },
//   ];

//   const managerPlan = {
//     id: "pm_access_management_24_95",
//     title: "Tenant Access - General Access Subscription",
//     priceStr: "$24.95",
//     cadence: "/ month",
//     badge: "Managers",
//     subtitle: "Flat rate for general access + $9.95 per property/per month.",
//     bullets: [
//       "Full access management suite",
//       "Automated recurring billing",
//       "Portfolio & team ready",
//       "Priority support",
//     ],
//     cta: "Start Manager Plan",
//   };

//   // -------- Local Owner Checkout (minimal; keeps landlord flow unchanged) ----
//   const [ownerSubmitting, setOwnerSubmitting] = useState(false);
//   const [ownerError, setOwnerError] = useState("");

//   const startOwnerCheckout = async (planKey) => {
//     try {
//       setOwnerError("");
//       if (!currentUser?.uid) {
//         setOwnerError("User authentication is missing.");
//         return;
//       }
//       setOwnerSubmitting(true);

//       // Call your new owner endpoint
//       const resp = await axios.post(`${API_URL}/api/subscribe/owner`, {
//         uid: currentUser.uid,
//         plan: planKey, // "owner_basic" | "owner_warranty"
//         billingCycle: "monthly",
//       });

//       const { sessionId } = resp.data || {};
//       if (!sessionId) throw new Error("No session ID returned from API.");

//       const stripe = await loadStripe(STRIPE_PUBLIC_KEY);
//       if (!stripe) throw new Error("Stripe.js failed to load!");

//       const { error } = await stripe.redirectToCheckout({ sessionId });
//       if (error) setOwnerError(error.message || "Checkout failed.");
//     } catch (e) {
//       setOwnerError(e?.response?.data?.error || e.message || "Checkout failed.");
//     } finally {
//       setOwnerSubmitting(false);
//     }
//   };

//   // -------- Handlers ---------------------------------------------------------
//   const handleTab = (_e, value) => value && setActiveTab(value);

//   // keep hook signature untouched; landlord/manager plan keeps using the existing flow
//   const startMonthlySubscription = () => startSubscription("monthly");

//   // -------- Reusable bits ----------------------------------------------------
//   const FeatureList = ({ items }) => (
//     <List sx={PricingPageStyles.featuresList}>
//       {items.map((txt, i) => (
//         <ListItem key={i} sx={PricingPageStyles.featureItem}>
//           <ListItemIcon sx={PricingPageStyles.featureIcon}>
//             <CheckCircleRoundedIcon />
//           </ListItemIcon>
//           <ListItemText primary={txt} />
//         </ListItem>
//       ))}
//     </List>
//   );

//   const PlanCard = ({ plan, color = "primary" }) => {
//     // decide which loader/error to reflect per plan type
//     const isOwner = isOwnerPlan(plan.id);
//     const isBusy = isOwner ? ownerSubmitting : hookLoading;

//     const onClick = () => {
//       if (isOwner) {
//         // map to backend key if provided
//         const planKey = plan.planKey || (plan.id.includes("warranty") ? "owner_warranty" : "owner_basic");
//         return startOwnerCheckout(planKey);
//       }
//       // manager plan -> existing landlord/agent flow
//       if (isSubscriptionPlan(plan.id)) return startMonthlySubscription();
//       return null;
//     };

//     return (
//       <Card elevation={12} sx={PricingPageStyles.planCard}>
//         <CardContent>
//           <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
//             <Typography variant="h5" sx={PricingPageStyles.planTitle}>
//               {plan.title}
//             </Typography>
//             {plan.badge && <Chip color={color} label={plan.badge} sx={PricingPageStyles.badge} />}
//           </Stack>

//           <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 0.5 }}>
//             <Typography variant="h3" sx={PricingPageStyles.planPrice}>
//               {plan.priceStr}
//             </Typography>
//             <Typography variant="subtitle1" color="text.secondary">
//               {plan.cadence}
//             </Typography>
//           </Stack>

//           {plan.setup && (
//             <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
//               {plan.setup}
//             </Typography>
//           )}

//           <Typography variant="body1" sx={PricingPageStyles.planSubtitle}>
//             {plan.subtitle}
//           </Typography>

//           <Divider sx={PricingPageStyles.divider} />

//           <FeatureList items={plan.bullets} />
//         </CardContent>

//         <CardActions sx={PricingPageStyles.cardActions}>
//           <Button
//             onClick={onClick}
//             variant="contained"
//             size="large"
//             fullWidth
//             disabled={isBusy}
//             sx={PricingPageStyles.ctaButton}
//           >
//             {isBusy ? "Processing..." : plan.cta}
//           </Button>
//         </CardActions>
//       </Card>
//     );
//   };

//   const OwnersSection = () => (
//     <Grid container spacing={3} justifyContent="center">
//       {ownerPlans.map((p, i) => (
//         <Grid item xs={12} md={6} lg={5} key={p.id}>
//           <PlanCard plan={p} color={i === 1 ? "success" : "primary"} />
//         </Grid>
//       ))}
//     </Grid>
//   );

//   const ManagersSection = () => (
//     <Grid container spacing={3} justifyContent="center">
//       <Grid item xs={12} md={7} lg={6}>
//         <PlanCard plan={managerPlan} color="info" />
//       </Grid>
//     </Grid>
//   );

//   // -------- Render (Auth gate) ----------------------------------------------
//   if (!authChecked || roleLoading) {
//     return (
//       <Box sx={PricingPageStyles.pageBg(theme)}>
//         <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
//           <Stack spacing={2}>
//             <Skeleton variant="text" width={240} height={56} />
//             <Skeleton variant="rounded" height={40} width={260} />
//             <Skeleton variant="rounded" height={220} />
//           </Stack>
//         </Container>
//       </Box>
//     );
//   }

//   if (!authed) {
//     return (
//       <Box sx={PricingPageStyles.pageBg(theme)}>
//         <Container maxWidth="sm" sx={{ py: { xs: 8, md: 12 } }}>
//           <Card elevation={10} sx={{ p: 4, textAlign: "center", borderRadius: 4 }}>
//             <Typography variant="h4" sx={{ mb: 1 }}>
//               Please log in to view pricing
//             </Typography>
//             <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
//               You need an account to see subscription options.
//             </Typography>
//             <Button
//               component={RouterLink}
//               to="/login"
//               size="large"
//               variant="contained"
//               sx={PricingPageStyles.ctaButton}
//             >
//               Go to Login
//             </Button>
//           </Card>
//         </Container>
//       </Box>
//     );
//   }

//   return (
//     <Box sx={PricingPageStyles.pageBg(theme)}>
//       <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
//         <Typography variant="h2" sx={PricingPageStyles.pageTitle}>
//           Pricing
//         </Typography>
//         <Typography variant="h6" sx={PricingPageStyles.pageSubtitle}>
//           Clear options for agents (landlords) and managers — straightforward subscriptions.
//         </Typography>

//         {/* Tabs for the relevant audience only */}
//         <Box sx={PricingPageStyles.tabsWrap}>
//           <Tabs
//             value={activeTab}
//             onChange={handleTab}
//             variant="scrollable"
//             scrollButtons="auto"
//             sx={PricingPageStyles.tabs}
//           >
//             {visibleTabs.map((t) => (
//               <Tab
//                 key={t.key}
//                 value={t.key}
//                 icon={t.icon}
//                 iconPosition="start"
//                 label={t.label}
//                 sx={PricingPageStyles.tab}
//               />
//             ))}
//           </Tabs>
//         </Box>

//         <Box sx={{ mt: 5 }}>
//           {activeTab === "owners" && <OwnersSection />}
//           {activeTab === "managers" && <ManagersSection />}
//         </Box>

//         {(hookError || ownerError) && (
//           <Typography color="error" sx={{ mt: 3, textAlign: "center" }}>
//             {ownerError || hookError}
//           </Typography>
//         )}
//       </Container>
//     </Box>
//   );
// };

// export default PricingPage;


